import React, { useEffect, useState } from "react";
import { Checkbox, Progress, Avatar, Button, Flex, Modal, List } from "antd";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { UserOutlined } from "@ant-design/icons";
import moment from "moment";
import { checkIfContactSaved } from "../../../utils";
import { useSocket } from "../../../context/SocketContext";
import { RootState } from "../../../redux/store";
import ContactCard from "../ContactCard";
import { storageService } from "../../../services/storageService";
import apiInstance from "../../../services/apiService";
import "./index.scss";

interface PollBubbleProps {
  isUser: boolean;
  msgId: string;
  question: string;
  multiple?: boolean;
  options: any[];
  timestamp: string;
}

const formatCount = (n: number): string => {
  if (n >= 1_000_000) return `${+(n / 1_000_000).toFixed(1)}m`;
  if (n >= 1_000) return `${+(n / 1_000).toFixed(1)}k`;
  return String(n);
};

const PollBubble: React.FC<PollBubbleProps> = ({
  isUser,
  msgId,
  question,
  multiple = false,
  options,
  timestamp,
}) => {
  const { socket } = useSocket();
  const { contacts } = useSelector((state: RootState) => state.contacts);
  const { currentChat } = useSelector((state: RootState) => state.chat);
  const [selected, setSelected] = useState<any>(multiple ? [] : null);
  const [loading, setLoading] = useState(false);
  const [pollOptions, setPollOptions] = useState<any[]>([]);
  const [isVotesModalOpen, setIsVotesModalOpen] = useState<boolean>(false);

  const userId = storageService.getUserId();

  const handleVoteChange = async (optionId: string) => {
    if (loading || !optionId || !userId) return;

    // Snapshot before optimistic update (for rollback on error)
    const prevOptions = pollOptions;
    const voterEntry = { member: userId, votedAt: new Date().toISOString() };

    // Optimistically update pollOptions (members + count) immediately
    const updatedOptions = pollOptions.map((opt: any) => {
      const alreadyVoted = opt.members?.some((m: any) => m.member === userId);

      if (opt._id === optionId) {
        // Toggle: add or remove the user from this option
        const newMembers = alreadyVoted
          ? (opt.members || []).filter((m: any) => m.member !== userId)
          : [...(opt.members || []), voterEntry];
        return { ...opt, members: newMembers };
      }

      if (!multiple && alreadyVoted) {
        // Single-select: remove user from previously selected option
        return { ...opt, members: (opt.members || []).filter((m: any) => m.member !== userId) };
      }

      return opt;
    });

    setPollOptions(updatedOptions);
    setLoading(true);

    const payload = {
      chatId: currentChat?.id,
      msgId,
      qId: optionId,
    };

    try {
      await apiInstance.post("chat/vote-polling", payload);
    } catch (error: any) {
      toast.error(error.message || "Failed to update vote");
      // Revert to snapshot
      setPollOptions(prevOptions);
    } finally {
      setLoading(false);
    }
  };

  const getVoterDetails = (voter: any) => {
    const voterDetails = currentChat?.groupMembers?.find(
      (item: any) => item._id === voter?.member,
    );

    const savedDetails = checkIfContactSaved(
      voterDetails?.phoneNumber,
      contacts,
    );

    return savedDetails;
  };

  const isSelected = (id: string) =>
    multiple ? selected.includes(id) : selected === id;

  useEffect(() => {
    const handlePollUpdate = (msg: any) => {
      if (msg?.message?._id === msgId) {
        const updatedOptions = msg?.message?.poll?.options;
        if (updatedOptions) setPollOptions(updatedOptions);
      }
    };

    socket?.on("poll-update", handlePollUpdate);

    return () => {
      socket?.off("poll-update", handlePollUpdate);
    };
  }, [socket, msgId]);

  useEffect(() => {
    if (options) {
      setPollOptions(options);
    }
  }, [options]);

  // Sync selected state from server data so deselections are reflected correctly
  useEffect(() => {
    if (!pollOptions?.length || !userId) return;
    if (multiple) {
      const voted = pollOptions
        .filter((opt) => opt.members?.some((m: any) => m.member === userId))
        .map((opt) => opt._id);
      setSelected(voted);
    } else {
      const voted = pollOptions.find((opt) =>
        opt.members?.some((m: any) => m.member === userId),
      );
      setSelected(voted?._id ?? null);
    }
  }, [pollOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className={`poll-bubble ${isUser ? "poll-user" : "poll-other"}`}>
        <div className="poll-header">
          <span className="poll-question">{question}</span>
          <span className="poll-subtitle">
            {multiple ? "Select multiple" : "Select one"}
          </span>
        </div>

        <div className="poll-options">
          {pollOptions?.map((opt) => {
            const voteCount = opt.members?.length ?? 0;
            return (
              <div
                key={opt._id}
                className="poll-option"
                onClick={() => handleVoteChange(opt._id)}
              >
                <Flex justify="space-between" align="center">
                  <Checkbox checked={isSelected(opt._id)}>
                    {opt.option}
                  </Checkbox>

                  <Flex align="center" gap={12} className="poll-vote-info">
                    {voteCount > 0 && voteCount <= 3 && (
                      <div className="poll-avatars">
                        {opt.members.map((voter: any, i: number) => (
                          <Avatar key={i} size={18}>
                            <UserOutlined />
                          </Avatar>
                        ))}
                      </div>
                    )}
                    <span className="vote-count">{formatCount(voteCount)}</span>
                  </Flex>
                </Flex>

                <div className="poll-progress">
                  <Progress
                    percent={isSelected(opt._id) ? 100 : 0}
                    showInfo={false}
                    strokeColor={
                      isSelected(opt._id) && isUser ? "#E6E9FF" : "#8FA2F8"
                    }
                    trailColor={isUser ? "#4C56C5" : "#E6E9FF"}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <Button
          type="text"
          className="view-votes"
          onClick={() => {
            setIsVotesModalOpen(true);
          }}
        >
          View votes
        </Button>
      </div>

      <Modal
        className="custom-modal voters-list-modal"
        title="Poll details"
        open={isVotesModalOpen}
        onCancel={() => setIsVotesModalOpen(false)}
        footer={null}
      >
        {(() => {
          const totalMembers = currentChat?.groupMembers?.length ?? 0;
          const voterIds = new Set(
            pollOptions.flatMap((opt: any) =>
              (opt.members ?? []).map((m: any) => m.member),
            ),
          );
          const totalVoted = voterIds.size;
          const maxVotes = Math.max(
            ...pollOptions.map((opt: any) => opt.members?.length ?? 0),
            0,
          );

          return (
            <Flex vertical gap={16} style={{ padding: "12px 0 4px" }}>
              <Flex vertical gap={4}>
                <span className="poll-details-question">{question}</span>
                <span className="poll-details-summary">
                  {totalVoted} of {totalMembers} member
                  {totalMembers !== 1 ? "s" : ""} voted
                </span>
              </Flex>

              <List
                dataSource={pollOptions}
                renderItem={(opt: any) => {
                  const voteCount = opt.members?.length ?? 0;
                  const isTop = voteCount > 0 && voteCount === maxVotes;
                  return (
                    <List.Item
                      key={opt._id}
                      style={{
                        flexDirection: "column",
                        alignItems: "stretch",
                        padding: "10px 0",
                      }}
                    >
                      <Flex
                        justify="space-between"
                        align="center"
                        style={{ marginBottom: voteCount > 0 ? 8 : 0 }}
                      >
                        <span className="poll-details-option-label">
                          {opt.option}
                        </span>
                        <span
                          className={`poll-vote-badge ${isTop && "poll-vote-badge--top"}`}
                        >
                          {isTop && <span className="poll-vote-star">★</span>}
                          {formatCount(voteCount)}{" "}
                          {voteCount === 1 ? "vote" : "votes"}
                        </span>
                      </Flex>
                      {voteCount > 0 && (
                        <List
                          split={false}
                          className="poll-voters-list"
                          dataSource={opt.members}
                          renderItem={(member: any, idx: number) => (
                            <List.Item
                              style={{
                                padding: "6px 0",
                                borderBlockEnd:
                                  idx === (opt.members?.length ?? 0) - 1
                                    ? "none"
                                    : "1px solid rgba(255,255,255,0.12)",
                              }}
                            >
                              <ContactCard
                                userName={getVoterDetails(member)}
                                phoneNumber={moment(member?.votedAt).format(
                                  "MMMM Do YYYY, h:mm A",
                                )}
                              />
                            </List.Item>
                          )}
                        />
                      )}
                    </List.Item>
                  );
                }}
              />
            </Flex>
          );
        })()}
      </Modal>
    </>
  );
};

export default PollBubble;
